import 'react-multi-carousel/lib/styles.css';

import type { BillingCycle, SubscriptionPlan } from '@billx/types';
import { getPlanDisplayPrice } from '@billx/utils';
import {
  alpha,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Skeleton,
  Typography,
  useTheme,
} from '@mui/material';
import { AnimatePresence, motion } from 'framer-motion';
import { CheckIcon } from 'lucide-react';
import React, { useState } from 'react';
import Carousel from 'react-multi-carousel';

import { CustomToggleButtonGroup } from '../CustomToggleButtonGroup';

const formatEntitlement = (key: string, ent: any): string | null => {
  const capitalize = (str: string) => str.replace(/^./, (c) => c.toUpperCase());
  const formatKey = (k: string) => capitalize(k.replace(/([A-Z])/g, ' $1'));

  if (ent.type === 'boolean') {
    if (!ent.enabled) return null;
    return formatKey(key);
  }

  if (ent.type === 'resource' || ent.type === 'concurrent') {
    if (ent.limit === -1) return `Unlimited ${formatKey(key)}`;
    return `${ent.limit} ${formatKey(key)}`;
  }

  if (ent.type === 'periodic') {
    const period = ent.period ? ent.period.replace('_', ' ') : 'period';
    if (ent.limit === -1) return `Unlimited ${formatKey(key)} / ${period}`;
    return `${ent.limit} ${formatKey(key)} / ${period}`;
  }

  if (ent.type === 'feature') {
    if (key === 'reports' || key === 'support') {
      if (ent.level === 'none' || ent.level === 'none ') return null;
      const freq = ent.frequency ? capitalize(ent.frequency) + ' ' : '';
      const lvl =
        ent.level && ent.level !== 'none' ? capitalize(ent.level) + ' ' : '';
      return `${freq}${lvl}${formatKey(key)}`;
    }
    return formatKey(key);
  }

  return null;
};

export interface PlanSelectionFormProps {
  plans: SubscriptionPlan[];
  isPlansLoading: boolean;
  onPlanSelect: (value: SubscriptionPlan) => void;
  activePlanId?: string;
}

const responsive = {
  sm: {
    breakpoint: { min: 0, max: 600 },
    items: 1,
    slidesToSlide: 1,
    partialVisibilityGutter: 40,
  },
  md: {
    breakpoint: { min: 600, max: 1024 },
    items: 2,
    slidesToSlide: 1,
  },
  lg: {
    breakpoint: { min: 1024, max: 1600 },
    items: 2.5,
    slidesToSlide: 1, // Changed to 1 for smoother sliding
  },
  xl: {
    breakpoint: { min: 1600, max: 2400 },
    items: 4,
    slidesToSlide: 1, // Changed to 1 for smoother sliding
  },
  xxl: {
    breakpoint: { min: 2400, max: 3000 },
    items: 5,
    slidesToSlide: 1, // Changed to 1 for smoother sliding
  },
};

export const PlanSelectionForm: React.FC<PlanSelectionFormProps> = ({
  plans,
  onPlanSelect,
  activePlanId,
  isPlansLoading,
}) => {
  const theme = useTheme();
  const [currentBillingCycle, setCurrentBillingCycle] =
    useState<BillingCycle>('monthly');

  const handleBillingCycleChange = (cycle: BillingCycle) => {
    setCurrentBillingCycle(cycle);
  };

  const filteredPlans = plans.filter(
    (plan) => plan.billingCycle === currentBillingCycle
  );

  // Sort plans by price to ensure logical order (Free -> Basic -> Pro)
  const sortedPlans = [...filteredPlans].sort((a, b) => a.price - b.price);

  return (
    <Box
      sx={{
        width: '100%',
        maxWidth: '100%',
        mx: 'auto',
        p: 1,
      }}
    >
      {/* Billing Cycle Toggle */}
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        mb={5}
        position="relative"
      >
        <Box position="relative">
          <CustomToggleButtonGroup
            value={currentBillingCycle}
            onChange={(value) =>
              handleBillingCycleChange(value as BillingCycle)
            }
            items={[
              {
                label: 'Monthly Billing',
                value: 'monthly',
              },
              {
                label: 'Yearly Billing',
                value: 'yearly',
              },
            ]}
          />
          <motion.div
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ type: 'spring', stiffness: 200, damping: 15 }}
          >
            <Chip
              label="Save up to 20%"
              size="small"
              color="success"
              variant="filled"
              sx={{
                position: 'absolute',
                top: -12,
                right: -80,
                fontSize: '0.6rem',
                height: 20,
                fontWeight: 700,
                zIndex: 2,
                boxShadow: `0 4px 12px ${alpha(theme.palette.success.main, 0.4)}`,
              }}
            />
          </motion.div>
        </Box>
      </Box>

      {/* Plans Carousel */}
      {isPlansLoading ? (
        <Box
          display="grid"
          gridTemplateColumns={{ xs: '1fr', md: 'repeat(3, 1fr)' }}
          gap={3}
        >
          {Array.from({ length: 3 }).map((_, index) => (
            <Card
              key={index}
              variant="outlined"
              sx={{ borderRadius: 4, userSelect: 'none' }}
            >
              <CardContent>
                <Skeleton variant="text" height={40} width="60%" />
                <Skeleton variant="text" height={60} width="80%" />
                <Box mt={4}>
                  <Skeleton variant="rectangular" height={200} />
                </Box>
              </CardContent>
            </Card>
          ))}
        </Box>
      ) : (
        <AnimatePresence mode="wait">
          <motion.div
            key={currentBillingCycle}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.3 }}
          >
            <Carousel
              responsive={responsive}
              partialVisible={true}
              className="plan-carousel"
              containerClass="carousel-container"
              itemClass="carousel-item-padding-40-px"
              renderButtonGroupOutside={true}
              arrows={false}
              showDots={true}
              renderDotsOutside={false}
            >
              {sortedPlans.map((plan) => {
                const isActive = activePlanId === plan.id;
                const isPopular = plan.popular;

                return (
                  <Box
                    key={plan.id}
                    sx={{
                      userSelect: 'none',
                      position: 'relative',
                      display: 'flex',
                      flexDirection: 'column',
                      height: '100%',
                      pt: 2,
                      pb: 4,
                      px: 2,
                    }}
                  >
                    {isPopular && (
                      <Chip
                        label="Most Popular"
                        color="primary"
                        sx={{
                          position: 'absolute',
                          top: 4,
                          left: '50%',
                          transform: 'translateX(-50%)',
                          zIndex: 2,
                          fontWeight: 700,
                          boxShadow: theme.shadows[4],
                        }}
                      />
                    )}

                    <Card
                      variant="outlined"
                      sx={{
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        borderRadius: 4,
                        transition: 'all 0.3s ease',
                        position: 'relative',
                        zIndex: 1,
                        borderColor: isActive ? 'primary.main' : 'divider',
                        borderWidth: isActive ? 2 : 1,
                        bgcolor: isActive ? 'primary.50' : 'background.paper',
                        boxShadow: isActive
                          ? theme.shadows[6]
                          : isPopular
                            ? `0 16px 40px ${alpha(theme.palette.primary.main, 0.15)}`
                            : 'none',
                        transform: isActive ? 'scale(1.02)' : 'scale(1)',
                        '&:hover': {
                          boxShadow: theme.shadows[8],
                          transform: 'translateY(-4px)',
                        },
                      }}
                    >
                      <CardContent
                        sx={{
                          flexGrow: 1,
                          display: 'flex',
                          flexDirection: 'column',
                          p: 4,
                          pb: '32px !important',
                        }}
                      >
                        {/* Header */}
                        <Box textAlign="center" mb={3}>
                          <Typography
                            variant="h5"
                            fontWeight={700}
                            gutterBottom
                          >
                            {plan.name}
                          </Typography>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            minHeight={40}
                          >
                            {plan.description}
                          </Typography>
                        </Box>

                        {/* Price */}
                        <Box
                          textAlign="center"
                          mb={4}
                          sx={{
                            p: 2,
                            bgcolor: isActive
                              ? 'background.paper'
                              : 'background.default',
                            borderRadius: 2,
                          }}
                        >
                          <Box
                            display="flex"
                            alignItems="baseline"
                            justifyContent="center"
                            gap={0.5}
                          >
                            <Typography variant="h3" fontWeight={800}>
                              {getPlanDisplayPrice(plan, currentBillingCycle)}
                            </Typography>
                            {plan.price > 0 && (
                              <Typography
                                variant="subtitle1"
                                color="text.secondary"
                                fontWeight={500}
                              >
                                /
                                {currentBillingCycle === 'yearly' ? 'mo' : 'mo'}
                              </Typography>
                            )}
                          </Box>
                          {plan.price > 0 &&
                            currentBillingCycle === 'yearly' && (
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                display="block"
                                mt={0.5}
                              >
                                Billed yearly
                              </Typography>
                            )}
                        </Box>

                        {/* Features */}
                        <Box
                          component="ul"
                          sx={{
                            listStyle: 'none',
                            p: 0,
                            m: 0,
                            mb: 4,
                            flexGrow: 1,
                          }}
                        >
                          {Object.entries(plan.entitlements)
                            .map(([key, ent]) => formatEntitlement(key, ent))
                            .filter((label): label is string => label !== null)
                            .map((label, idx) => (
                              <Box
                                component="li"
                                key={idx}
                                display="flex"
                                alignItems="flex-start"
                                mb={2}
                                gap={1.5}
                              >
                                <Box
                                  sx={{
                                    p: 0.5,
                                    borderRadius: '50%',
                                    bgcolor: 'primary.50',
                                    color: 'primary.main',
                                    display: 'flex',
                                  }}
                                >
                                  <CheckIcon size={14} strokeWidth={3} />
                                </Box>
                                <Typography variant="body2" fontWeight={500}>
                                  {label}
                                </Typography>
                              </Box>
                            ))}
                        </Box>

                        {/* Action Button */}
                        <Button
                          fullWidth
                          variant={
                            isActive
                              ? 'outlined'
                              : isPopular
                                ? 'contained'
                                : 'outlined'
                          }
                          color="primary"
                          size="large"
                          disabled={isActive}
                          onClick={() => onPlanSelect(plan)}
                          sx={{
                            borderRadius: 2,
                            py: 1.5,
                            fontWeight: 600,
                            textTransform: 'none',
                            fontSize: '1rem',
                            mt: 'auto',
                          }}
                        >
                          {isActive ? 'Current Plan' : 'Select Plan'}
                        </Button>
                      </CardContent>
                    </Card>
                  </Box>
                );
              })}
            </Carousel>
          </motion.div>
        </AnimatePresence>
      )}
    </Box>
  );
};
